// =====================================================================
// crmCustomer — MEDDPICCR tab editors
//
// All inline cards on the MEDDPICCR tab live here:
//   - OverrideDialog          force an element ON/OFF with attribution (P1.4)
//   - DecisionCriteriaEditor  free-text DC editor + "Pull from research" (P1.3)
//   - DecisionProcessEditor   free-text DP editor (P1.3)
//
// Pulled out of opportunities.jsx so the MEDDPICCR-tab surface area stays
// readable on its own. Loaded before opportunities.js so OppDetail can
// reference these components directly.
// =====================================================================

// ────────────────────────────────────────────────────────────────────
// OverrideDialog — force a MEDDPICCR element ON or OFF (or clear an
// existing override). Persisted into opp.meddpiccOverrides[<key>] with
// who / when / why attribution, per the P1.4 spec.
// ────────────────────────────────────────────────────────────────────
function OverrideDialog({ opp, elementKey, elementLabel, derivedEntry, onClose, onSaved }) {
  const auth = useAuth();
  const userId = (auth && auth.user && (auth.user.id || auth.user.email)) || "";
  const existing = (opp.meddpiccOverrides || {})[elementKey] || null;
  const hasOverride = !!(existing && (existing.source === "override-on" || existing.source === "override-off"));
  const derivedValue = !!(derivedEntry && derivedEntry.value);
  const [choice, setChoice] = useState(hasOverride ? existing.source : (derivedValue ? "override-off" : "override-on"));
  const [reason, setReason] = useState(hasOverride ? (existing.reason || "") : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save(action) {
    setSaving(true); setError(null);
    try {
      const overrides = Object.assign({}, opp.meddpiccOverrides || {});
      if (action === "clear") {
        delete overrides[elementKey];
      } else {
        if (!reason.trim()) {
          setError("A reason is required — the audit trail is the whole point of the override.");
          setSaving(false);
          return;
        }
        if (!userId) {
          setError("Cannot record an override without a signed-in user.");
          setSaving(false);
          return;
        }
        overrides[elementKey] = {
          value:  action === "override-on",
          source: action,
          by:     userId,
          at:     new Date().toISOString(),
          reason: reason.trim(),
        };
      }
      const updated = await tpAPI.opportunities.update(opp.id, { meddpiccOverrides: overrides });
      onSaved && onSaved(updated);
      onClose && onClose();
    } catch (e) {
      setError(e.message || "Failed to save override.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      title={`Override · ${elementLabel}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          {hasOverride && (
            <button className="btn btn-sm" onClick={() => save("clear")} disabled={saving} title="Revert to derived state">
              Clear override
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => save(choice)} disabled={saving}>
            {saving ? "Saving…" : (hasOverride ? "Update override" : "Save override")}
          </button>
        </>
      }
    >
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>
        Derived state: <strong>{derivedValue ? "ON" : "OFF"}</strong>.
        {hasOverride && <> Existing override by <strong>{existing.by}</strong> on {fmtDate(existing.at)}.</>}
      </div>
      <div className="form-field">
        <label>Override action</label>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <label style={{ fontSize: 13 }}>
            <input type="radio" checked={choice === "override-on"}  onChange={() => setChoice("override-on")} /> Force ON
          </label>
          <label style={{ fontSize: 13 }}>
            <input type="radio" checked={choice === "override-off"} onChange={() => setChoice("override-off")} /> Force OFF
          </label>
        </div>
      </div>
      <div className="form-field">
        <label>Reason (required)</label>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3}
          placeholder="Why is the derived rule wrong for this deal? What's the evidence?"
          style={{ width: "100%", fontFamily: "inherit", fontSize: 13 }} />
      </div>
      {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────
// DecisionCriteriaEditor — free-text criteria list for the deal (DC element).
// "Pull from research" aggregates pain / useCase / keyQuote from surveys
// and interviews linked to this opportunity, so the rep doesn't have to
// retype evidence already captured during discovery.
// ────────────────────────────────────────────────────────────────────
function DecisionCriteriaEditor({ opp, onChange }) {
  const initial = (opp && opp.decisionCriteria) || "";
  const [draft, setDraft] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setDraft((opp && opp.decisionCriteria) || ""); }, [opp.id, opp.decisionCriteria]);

  function pullFromResearch() {
    const oppSurveys = (D.SURVEYS || []).filter(s => s.opp === opp.id);
    const oppInterviews = (D.INTERVIEWS || []).filter(i => i.opp === opp.id);
    const lines = [];
    for (const s of oppSurveys) {
      if (s.pain || s.useCase) {
        const contact = (D.CONTACTS || []).find(c => c.id === s.contactId);
        const who = contact ? `${contact.first} ${contact.last}` : "Survey";
        if (s.pain)    lines.push(`· ${who} (survey) — pain: ${s.pain}`);
        if (s.useCase) lines.push(`· ${who} (survey) — use case: ${s.useCase}`);
      }
    }
    for (const i of oppInterviews) {
      if (i.pain || i.useCase || i.keyQuote) {
        const contact = (D.CONTACTS || []).find(c => c.id === i.contactId);
        const who = contact ? `${contact.first} ${contact.last}` : "Interview";
        if (i.pain)     lines.push(`· ${who} (interview) — pain: ${i.pain}`);
        if (i.useCase)  lines.push(`· ${who} (interview) — use case: ${i.useCase}`);
        if (i.keyQuote) lines.push(`· ${who} (interview) — quote: "${i.keyQuote}"`);
      }
    }
    if (lines.length === 0) {
      setError("No pain / useCase / quote captured on linked surveys or interviews yet.");
      return;
    }
    setError(null);
    const pulled = lines.join("\n");
    setDraft(prev => prev && prev.trim() ? prev + "\n\n" + pulled : pulled);
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      const value = draft && draft.trim() ? draft : null;
      const updated = await tpAPI.opportunities.update(opp.id, { decisionCriteria: value });
      onChange && onChange(updated);
      setEditing(false);
    } catch (e) {
      setError(e.message || "Failed to save decision criteria.");
    } finally {
      setSaving(false);
    }
  }

  const hasContent = !!(initial && initial.trim());

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div className="card-title">Decision Criteria (MEDDPICCR · D)</div>
          <div className="card-sub">DC lights up the moment any criteria are written here.</div>
        </div>
        <div className="flex-gap" style={{ gap: 6 }}>
          {!editing && <button className="btn btn-sm" onClick={() => setEditing(true)}>{hasContent ? "Edit" : "Add criteria"}</button>}
          {editing && (
            <>
              <button className="btn btn-sm" onClick={pullFromResearch} disabled={saving}>Pull from research</button>
              <button className="btn btn-sm" onClick={() => { setDraft(initial); setEditing(false); setError(null); }} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            </>
          )}
        </div>
      </div>
      <div className="card-body">
        {!editing ? (
          hasContent
            ? <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit", fontSize: 13 }}>{initial}</pre>
            : <div className="muted" style={{ fontSize: 13 }}>No criteria captured yet — DC is OFF.</div>
        ) : (
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={6}
            style={{ width: "100%", fontFamily: "inherit", fontSize: 13 }}
            placeholder="Criteria the buyer will judge solutions against. e.g.&#10;· Must integrate with their SSO&#10;· Compliance certs (SOC2, ISO27001)"
          />
        )}
        {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// DecisionProcessEditor — free-text process notes (steps + timeline +
// named stakeholders). DP lights up when this is non-empty.
// ────────────────────────────────────────────────────────────────────
function DecisionProcessEditor({ opp, onChange }) {
  const initial = (opp && opp.decisionProcess) || "";
  const [draft, setDraft] = useState(initial);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setDraft((opp && opp.decisionProcess) || ""); }, [opp.id, opp.decisionProcess]);

  async function save() {
    setSaving(true); setError(null);
    try {
      const value = draft && draft.trim() ? draft : null;
      const updated = await tpAPI.opportunities.update(opp.id, { decisionProcess: value });
      onChange && onChange(updated);
      setEditing(false);
    } catch (e) {
      setError(e.message || "Failed to save decision process.");
    } finally {
      setSaving(false);
    }
  }

  const hasContent = !!(initial && initial.trim());

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div className="card-title">Decision Process (MEDDPICCR · D)</div>
          <div className="card-sub">Capture the steps the buyer will take to close, the timeline, and named stakeholders at each gate. DP lights up when this is non-empty.</div>
        </div>
        <div className="flex-gap" style={{ gap: 6 }}>
          {!editing && <button className="btn btn-sm" onClick={() => setEditing(true)}>{hasContent ? "Edit" : "Add process"}</button>}
          {editing && (
            <>
              <button className="btn btn-sm" onClick={() => { setDraft(initial); setEditing(false); setError(null); }} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            </>
          )}
        </div>
      </div>
      <div className="card-body">
        {!editing ? (
          hasContent
            ? <pre style={{ whiteSpace: "pre-wrap", margin: 0, fontFamily: "inherit", fontSize: 13 }}>{initial}</pre>
            : <div className="muted" style={{ fontSize: 13 }}>No process captured yet — DP is OFF.</div>
        ) : (
          <textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={8}
            style={{ width: "100%", fontFamily: "inherit", fontSize: 13 }}
            placeholder="e.g.&#10;1. Technical eval (eng. lead) — week 1&#10;2. Security review (CISO sign-off) — week 2&#10;3. Procurement / paper — weeks 3–4&#10;4. Final approval (CFO) — end of month"
          />
        )}
        {error && <div className="form-error" style={{ marginTop: 8 }}>{error}</div>}
      </div>
    </div>
  );
}

window.OverrideDialog = OverrideDialog;
window.DecisionCriteriaEditor = DecisionCriteriaEditor;
window.DecisionProcessEditor = DecisionProcessEditor;
